import { AdjustmentsHorizontalIcon, FunnelIcon, TruckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { PrimaryButton } from '../../components/buttons';
import { DownloadUploaderButton } from '../../components/download-uploader-button';
import { EditColumns } from '../../components/edit-columns';
import { EmptyState } from '../../components/empty-state';
import { Checkbox } from '../../components/forms';
import { FilterWrapper } from '../../components/product-table/filters/filter-wrapper';
import { SearchInput } from '../../components/search-input';
import { SortPopover } from '../../components/sort-popover';
import { TanStackTable } from '../../components/tanstack-table/table';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { ToolbarPopover } from '../../components/toolbar/popover';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import type { Vendor } from '../../data/vendor';
import { activeFilterCount, resetFilters } from '../../hooks/filter-fields';
import { useProducts } from '../../hooks/products';
import { useSearchFocus } from '../../hooks/use-search-focus';
import { useVisibleColumns } from '../../hooks/use-visible-columns';
import { useVendorIntegrations, useVendors } from '../../hooks/vendors';
import { storeLayout } from '../../route-apis';
import {
  getVendorColumns,
  NAME_COLUMN_ID,
  VENDOR_COLUMNS_KEY,
  VENDOR_PINNED_COLUMNS,
  VENDOR_SORT_OPTIONS,
} from './columns';
import {
  defaultVendorFilters,
  filterVendorRows,
  useVendorRows,
  type VendorFilters,
} from './vendor-rows';

const FILTER_FIELDS: (keyof VendorFilters)[] = ['hideDisabled'];

const columnConfig = { pinnedColumns: VENDOR_PINNED_COLUMNS };

export function Vendors() {
  const { vendors } = useVendors();
  const { products } = useProducts();
  const integrations = useVendorIntegrations();
  const { store } = storeLayout.useRouteContext();

  const navigate = useNavigate();
  const editVendor = useCallback(
    (vendor: Vendor) => {
      navigate({
        to: '/stores/$storeId/vendors/$vendorId/edit',
        params: { storeId: store.id, vendorId: vendor.vendor_id },
      });
    },
    [navigate, store]
  );

  const [filters, setFilters] = useState<VendorFilters>(defaultVendorFilters);
  const [sorting, setSorting] = useState<SortingState>([{ id: NAME_COLUMN_ID, desc: false }]);
  const searchReference = useSearchFocus();

  const rows = useVendorRows(vendors, products, integrations);
  const filteredRows = useMemo(() => filterVendorRows(rows, filters), [rows, filters]);

  const allColumns = useMemo(() => getVendorColumns({ onEdit: editVendor }), [editVendor]);
  const { columns, columnVisibility, columnOrder } = useVisibleColumns(
    allColumns,
    VENDOR_COLUMNS_KEY,
    columnConfig
  );

  const badge = activeFilterCount(filters, defaultVendorFilters, FILTER_FIELDS);
  const clearAll = useCallback(
    () => setFilters((current) => resetFilters(current, defaultVendorFilters, FILTER_FIELDS)),
    []
  );

  if (vendors.length === 0) {
    return store.last_uploaded_at == null ? (
      <EmptyState
        icon={<TruckIcon className="size-8" />}
        title="You haven't imported any vendors yet."
        description="Install the uploader to start syncing your LiquorPOS vendors."
        action={<DownloadUploaderButton primary />}
        footnote="Vendors appear here once you record purchases in LiquorPOS and the uploader is syncing."
      />
    ) : (
      <EmptyState
        icon={<TruckIcon className="size-8" />}
        title="No vendors yet."
        description="Vendors will appear here once you record purchases in LiquorPOS and your next upload runs."
      />
    );
  }

  return (
    <>
      <PageToolbar
        left={
          <>
            <ToolbarPopover
              icon={<AdjustmentsHorizontalIcon className="size-5" />}
              label="Filters"
              badge={badge}
              onClear={clearAll}
            >
              <FilterWrapper label="Hide Disabled">
                <Checkbox
                  invalid={false}
                  checked={filters.hideDisabled}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, hideDisabled: event.target.checked }))
                  }
                />
              </FilterWrapper>
            </ToolbarPopover>
            <SortPopover
              sorting={sorting}
              onSortingChange={setSorting}
              sortColumnOptions={VENDOR_SORT_OPTIONS}
            />
            <EditColumns
              tableKey={VENDOR_COLUMNS_KEY}
              pinnedColumns={VENDOR_PINNED_COLUMNS}
              sorting={sorting}
            />
          </>
        }
        right={
          <SearchWithCount quantity={filteredRows.length} total={rows.length} noun="vendors">
            <SearchInput
              grouped
              value={filters.query}
              onChange={(event) =>
                setFilters((current) => ({ ...current, query: event.target.value }))
              }
              inputRef={searchReference}
              placeholder="Search vendors"
              shortcut="meta+f"
            />
          </SearchWithCount>
        }
      />
      <TanStackTable
        data={filteredRows}
        columns={columns}
        sorting={sorting}
        onSortingChange={setSorting}
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        getRowId={(row) => row.vendor.vendor_id}
        emptyState={
          <EmptyState
            icon={<FunnelIcon className="size-8" />}
            title="No vendors match these filters."
            description={
              rows.length > 0 ? (
                <span className="tabular-nums">
                  0 of{' '}
                  <span className="font-semibold text-gray-700">
                    {rows.length.toLocaleString()}
                  </span>{' '}
                  vendors
                </span>
              ) : undefined
            }
            action={
              <PrimaryButton
                onClick={() => {
                  clearAll();
                  setFilters((current) => ({ ...current, query: '' }));
                }}
              >
                Clear all filters
              </PrimaryButton>
            }
          />
        }
      />
    </>
  );
}
